import { Stack, Step, StepLabel, Stepper, Theme, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import useAuth from 'hooks/useAuth';
import useUser from 'hooks/useUser';
import { useSnackbar } from 'notistack';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { getErrorMessage } from 'utils/stringUtils';
import useIntakeForm from 'views/pages/client-record/useIntakeForm';
import EducationalHistory from './EducationalHistory';
import FamilyMentalHealthHistory from './FamilyMentalHealthHistory';
import HealthAndSocialInformation from './HealthAndSocialInformation';
import MentalHealthHistory from './MentalHealthHistory';
import OccupationalInformation from './OccupationalInformation';
import PersonalInformation from './PersonalInformation';
import SocialHistory from './SocialHistory';
import TreatmentHistory from './TreatmentHistory';
import WhereYouAre from './WhereYouAre';

interface Props {
  userId: string;
  onFinish: () => void;
  activeStepData?: any;
  setActiveStepData?: any;
  activeStep?: any;
  setActiveStep?: any;
}

export default function IntakeFormWizard({ userId, onFinish, activeStepData, setActiveStepData, activeStep, setActiveStep }: Props) {
  const intl = useIntl();
  const steps = [
    intl.formatMessage({ defaultMessage: 'Personal Information' }),
    intl.formatMessage({ defaultMessage: "Why you're here" }),
    intl.formatMessage({ defaultMessage: 'Treatment History' }),
    intl.formatMessage({ defaultMessage: 'Mental Health History' }),
    intl.formatMessage({ defaultMessage: 'Social History' }),
    intl.formatMessage({ defaultMessage: 'Educational History' }),
    intl.formatMessage({ defaultMessage: 'Occupational Information' }),
    intl.formatMessage({ defaultMessage: 'Family Mental Health History' }),
    intl.formatMessage({ defaultMessage: 'Health And Social Information' })
  ];

  const { user, refetchUser } = useUser(userId);
  const { intakeForm, refetchIntakeForm } = useIntakeForm(userId);
  const { request } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const matches = useMediaQuery('(max-width:767px)');

  const stepperRef = useRef<any>();
  const divClientWidth = stepperRef.current?.clientWidth;

  const useStyles = makeStyles((theme: Theme) => ({
    intakeFormStepper: {
      '&  .MuiStep-horizontal': {
        flex: matches && '0 0 25%',
        maxWidth: matches && '25%'
      }
    }
  }));

  const classes = useStyles();

  function autoScroll() {
    let i: number;
    const scrollView: any = document.getElementsByClassName('MuiStepper-horizontal');

    for (i = 0; i < scrollView.length; i++) {
      const slide = scrollView[i];

      if (slide instanceof HTMLElement) {
        if (matches && activeStep === 0) {
          slide.scrollLeft = 0;
        } else if (matches && activeStep === 3) {
          slide.scrollLeft = divClientWidth * 0.3;
        } else if (matches && activeStep === 6) {
          slide.scrollLeft = divClientWidth * 0.7;
        } else if (matches && activeStep === 8) {
          slide.scrollLeft = divClientWidth;
        }
      }
    }
  }

  useEffect(() => {
    autoScroll();
    // eslint-disable-next-line
  }, [activeStep]);

  const updateIntakeForm = useMutation(
    (variables: { data: any; moveToNextStep?: boolean }) => request.put(`/user/${userId}/documents/intake-form`, variables.data),
    {
      onSuccess: (_, variables) => {
        if (variables.moveToNextStep) {
          setActiveStep((prevStep) => prevStep + 1);
        }
        refetchIntakeForm();
      },
      onError: (e) => {
        enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
      }
    }
  );

  const updateUserMutation = useMutation((variables: any) => request.put(`/user/${user?.id}`, variables), {
    onSuccess: () => {
      // onSubmitSuccess();
      refetchUser();
    }
  });

  const handleStep = (step: number, data?: any) => () => {
    if (activeStep === 0) {
      updateUserMutation.mutate(activeStepData?.data);
    }
    updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
    const stepperScroll = document.getElementById('stepperScroll');
    stepperScroll?.scrollTo(125 * (step - 1), 0);
    setActiveStep(step);
  };

  if (!user || !intakeForm) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Typography sx={{ fontStyle: 'italic' }}>
        <FormattedMessage
          defaultMessage={
            'Please provide the following information for our records. Leave blank any question you would rather not answer, or would prefer to discuss with your Provider. Information you provide here is held to the same standards of confidentiality as our therapy.'
          }
        />
      </Typography>
      <Stepper
        className={classes.intakeFormStepper}
        ref={stepperRef}
        activeStep={activeStep}
        alternativeLabel
        nonLinear
        sx={{ overflowX: 'scroll', paddingBottom: 4 }}
        id="stepperScroll"
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              onClick={handleStep(index)}
              sx={{
                border: '1px solid grey',
                marginX: 2,
                cursor: 'pointer',
                zIndex: 3,
                position: 'relative',
                backgroundColor: 'white',
                padding: 2,
                borderRadius: 2,
                height: 125,
                width: 125
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === 0 && (
        <PersonalInformation
          user={user}
          refetchUser={refetchUser}
          onSubmitSuccess={() => setActiveStep(activeStep + 1)}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 1 && (
        <WhereYouAre
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 2 && (
        <TreatmentHistory
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 3 && (
        <MentalHealthHistory
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 4 && (
        <SocialHistory
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 5 && (
        <EducationalHistory
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 6 && (
        <OccupationalInformation
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 7 && (
        <FamilyMentalHealthHistory
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate({ data, moveToNextStep: true })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
      {activeStep === 8 && (
        <HealthAndSocialInformation
          intakeForm={intakeForm}
          onPrevious={() => {
            setActiveStep(activeStep - 1);
            updateIntakeForm.mutate({ data: activeStepData, moveToNextStep: false });
          }}
          isSubmitting={updateIntakeForm.isLoading}
          onSubmit={(data) => updateIntakeForm.mutate(data, { onSuccess: onFinish })}
          handleStep={(data) => {
            setActiveStepData(data);
          }}
        />
      )}
    </Stack>
  );
}
